import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES, ERROR_MESSAGES } from './constants';

export const validateFile = (file: File | null): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!file) {
    errors.push('No file selected');
    return { isValid: false, errors };
  }

  if (file.size > MAX_FILE_SIZE) {
    errors.push(ERROR_MESSAGES.FILE_TOO_LARGE);
  }

  const fileName = file.name.toLowerCase();
  const isValidMimeType = ALLOWED_FILE_TYPES.includes(file.type);
  const isValidExtension = fileName.endsWith('.pdf') || 
                          fileName.endsWith('.docx') || 
                          fileName.endsWith('.txt') || 
                          fileName.endsWith('.md') || 
                          fileName.endsWith('.markdown');

  if (!isValidMimeType && !isValidExtension) {
    errors.push(ERROR_MESSAGES.INVALID_FILE_TYPE);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const generateJobId = (): string => {
  return `job_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

export const formatTimestamp = (timestamp: string | number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getUserId = (): string => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload) as Record<string, unknown>;
      return (payload.sub || payload.userId || payload.email || 'anonymous') as string;
    } catch (_e) {
      // Failed to decode auth token
    }
  }

  const sessionUserId = window.sessionStorage.getItem('user_id');
  if (sessionUserId) {
    return sessionUserId;
  }

  let localUserId = localStorage.getItem('user_id');
  if (!localUserId) {
    localUserId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem('user_id', localUserId);
  }

  return localUserId;
};

export const debounce = <T extends (...args: unknown[]) => void>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const retryWithBackoff = async <T>(fn: () => Promise<T>, maxRetries: number = 3, baseDelay: number = 1000): Promise<T> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;

      const delay = baseDelay * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('retryWithBackoff: unreachable');
};

export const parseErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  const err = error as Record<string, unknown> | null;
  if (err?.message) {
    return err.message as string;
  }

  const resp = err?.response as Record<string, unknown> | null;
  if (resp?.data) {
    const data = resp.data as Record<string, unknown>;
    if (data.message) return data.message as string;
    if (data.error) return data.error as string;
  }

  return 'An unexpected error occurred';
};

export const storage = {
  get: <T>(key: string, defaultValue: T | null = null): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (_e) {
      return defaultValue;
    }
  },
  set: (key: string, value: unknown): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (_e) {
      // Failed to save to localStorage
    }
  },
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (_e) {
      // Failed to remove from localStorage
    }
  },
};

interface JobRecord {
  job_id: string;
  job_type: string;
  start_time: number;
  created_at: number;
  merged_from?: {
    original_job_id: string;
    revision_job_id: string;
  };
}

export const sessionStorage = {
  get: <T>(key: string, defaultValue: T | null = null): T | null => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (_e) {
      return defaultValue;
    }
  },
  set: (key: string, value: unknown): void => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_e) {
      // Failed to save to sessionStorage
    }
  },
  remove: (key: string): void => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_e) {
      // Failed to remove from sessionStorage
    }
  },
  clear: (): void => {
    try {
      window.sessionStorage.clear();
    } catch (_e) {
      // Failed to clear sessionStorage
    }
  },
};

export const jobSession = {
  save: (jobId: string, jobType: string, startTime: number = Date.now()): void => {
    const currentJob: JobRecord = {
      job_id: jobId,
      job_type: jobType,
      start_time: startTime,
      created_at: Date.now()
    };
    
    sessionStorage.set('current_job', currentJob);
    
    if (jobType === 'revision') {
      sessionStorage.set('revision_job', currentJob);
    }
    
    if (jobType === 'generation') {
      sessionStorage.set('original_job', currentJob);
    }
  },
  
  saveOriginal: (jobId: string, jobType: string, startTime: number = Date.now()): void => {
    sessionStorage.set('original_job', {
      job_id: jobId,
      job_type: jobType,
      start_time: startTime,
      created_at: Date.now()
    });
  },

  saveMerged: (jobId: string, originalJobId: string, revisionJobId: string, startTime: number = Date.now()): void => {
    const mergedJob: JobRecord = {
      job_id: jobId,
      job_type: 'merge',
      start_time: startTime,
      created_at: Date.now(),
      merged_from: {
        original_job_id: originalJobId,
        revision_job_id: revisionJobId
      }
    };
    
    const currentRevision = sessionStorage.get<JobRecord>('revision_job');
    if (currentRevision) {
      sessionStorage.set('previous_job', currentRevision);
    }
    
    sessionStorage.set('current_job', mergedJob);
    sessionStorage.set('merged_job', mergedJob);
  },

  promoteMergedToBaseline: (): void => {
    const current = sessionStorage.get<JobRecord>('current_job');
    if (current) {
      sessionStorage.set('original_job', current);
    }
    sessionStorage.remove('revision_job');
    sessionStorage.remove('previous_job');
    sessionStorage.remove('merged_job');
  },

  getBaseline: (): JobRecord | null => {
    const merged = sessionStorage.get<JobRecord>('merged_job');
    if (merged) {
      return merged;
    }
    return sessionStorage.get<JobRecord>('original_job');
  },
  
  getLatestJobId: (): string | null => {
    const merged = sessionStorage.get<JobRecord>('merged_job');
    if (merged?.job_id) {
      return merged.job_id;
    }
    const current = sessionStorage.get<JobRecord>('current_job');
    if (current?.job_id) {
      return current.job_id;
    }
    const original = sessionStorage.get<JobRecord>('original_job');
    return original?.job_id || null;
  },
  
  get: (): JobRecord | null => {
    return sessionStorage.get<JobRecord>('current_job');
  },
  
  getOriginal: (): JobRecord | null => {
    return sessionStorage.get<JobRecord>('original_job');
  },
  
  getRevision: (): JobRecord | null => {
    return sessionStorage.get<JobRecord>('revision_job');
  },

  getMerged: (): JobRecord | null => {
    return sessionStorage.get<JobRecord>('merged_job');
  },

  getPrevious: (): JobRecord | null => {
    return sessionStorage.get<JobRecord>('previous_job');
  },
  
  clear: (): void => {
    sessionStorage.remove('current_job');
    sessionStorage.remove('original_job');
    sessionStorage.remove('revision_job');
    sessionStorage.remove('merged_job');
    sessionStorage.remove('previous_job');
  },
  
  exists: (): boolean => {
    return sessionStorage.get<JobRecord>('current_job') !== null;
  },
  
  hasVersions: (): boolean => {
    const merged = sessionStorage.get<JobRecord>('merged_job');
    const previous = sessionStorage.get<JobRecord>('previous_job');
    const original = sessionStorage.get<JobRecord>('original_job');
    const revision = sessionStorage.get<JobRecord>('revision_job');
    
    if (merged) {
      return previous !== null;
    }
    
    return original !== null && revision !== null;
  },

  hasMergedStudy: (): boolean => {
    return sessionStorage.get<JobRecord>('merged_job') !== null;
  },

  getVersionInfo: (): { hasMerged: boolean; hasOriginal: boolean; hasRevision: boolean; hasPrevious: boolean; currentJobType: string } => {
    const merged = sessionStorage.get<JobRecord>('merged_job');
    const previous = sessionStorage.get<JobRecord>('previous_job');
    const original = sessionStorage.get<JobRecord>('original_job');
    const revision = sessionStorage.get<JobRecord>('revision_job');
    const current = sessionStorage.get<JobRecord>('current_job');

    return {
      hasMerged: merged !== null,
      hasOriginal: original !== null,
      hasRevision: revision !== null,
      hasPrevious: previous !== null,
      currentJobType: current?.job_type || 'unknown'
    };
  }
};

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const calculateProgress = (startTime: number, estimatedDuration: number): number => {
  const elapsed = Date.now() - startTime;
  const progress = Math.min(95, (elapsed / estimatedDuration) * 100);
  return Math.round(progress);
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const logger = {
  log: (...args: unknown[]): void => {
    if (import.meta.env.DEV) {
      console.log('[App]', ...args);
    }
  },
  error: (...args: unknown[]): void => {
    if (import.meta.env.DEV) {
      console.error('[App Error]', ...args);
    }
  },
  warn: (...args: unknown[]): void => {
    if (import.meta.env.DEV) {
      console.warn('[App Warning]', ...args);
    }
  },
};
